// frontend/src/api.js

export function getApiBase() {
  const env =
    (import.meta?.env?.VITE_API_BASE_URL || import.meta?.env?.VITE_API_URL || "").trim();
  const runtime =
    (typeof window !== "undefined" && window.frontend && window.frontend.API_BASE) || "";
  return (env || runtime).replace(/\/+$/, "");
}

export const buildUrl = (path) => {
  const base = getApiBase();
  if (!base) {
    console.warn("[API] Empty API base, request will go to same-origin:", path);
  }

  const p = path.startsWith("/") ? path : `/${path}`;
  const baseNoSlash = String(base || "").replace(/\/+$/, "");
  if (baseNoSlash.endsWith("/api") && p.startsWith("/api/")) {
    return `${baseNoSlash}${p.slice(4)}`; // drop leading "/api"
  }

  return `${baseNoSlash}${p}`;
};

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function pickTokenFromObject(obj) {
  if (!obj || typeof obj !== "object") return "";
  return (
    obj.token ||
    obj.accessToken ||
    obj.access_token ||
    obj.jwt ||
    obj?.data?.token ||
    ""
  );
}

function formatApiError(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.message && typeof value.message === "string") return value.message;
  if (value.error && typeof value.error === "string") return value.error;
  if (value.code && typeof value.code === "string") return value.code;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function getTokenByRole(role) {
  const r = String(role || "").toLowerCase();

  const directKeysByRole = {
    // ВАЖНО: clientToken должен иметь абсолютный приоритет.
    // Нельзя подставлять providerToken/adminToken в клиентские запросы,
    // иначе после входа в разные роли браузер может отправить неверный JWT.
    client: ["clientToken", "token"],
    provider: ["providerToken", "token", "adminToken"],
    // Админ в Travella обычно тот же provider JWT с is_admin/roles.
    // Старый adminToken в браузере часто остаётся от другой среды и даёт Invalid token.
    admin: ["token", "providerToken", "adminToken"],
  };

  const directKeysAll = [
    "token",
    "adminToken",
    "providerToken",
    "clientToken",
    "accessToken",
    "jwt",
    "authToken",
  ];

  const keys = directKeysByRole[r] || directKeysAll;

  const pickFromStorage = (storage) => {
    for (const k of keys) {
      const v = storage.getItem(k);
      if (v && String(v).trim()) return String(v).trim();
    }
    return "";
  };

  // 1) localStorage direct keys
  let t = pickFromStorage(localStorage);
  if (t) return t;

  // 2) sessionStorage direct keys (иногда токен там)
  t = pickFromStorage(sessionStorage);
  if (t) return t;

  // 3) JSON objects (auth/user/session/admin/profile)
  const jsonKeys = ["auth", "user", "session", "admin", "profile"];
  for (const k of jsonKeys) {
    const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (!raw) continue;
    const obj = safeJsonParse(raw);
    const tt = pickTokenFromObject(obj);
    if (tt && String(tt).trim()) return String(tt).trim();
  }

  return "";
}


export function getAuthHeaders(role = null) {
  const token = getTokenByRole(role);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handle(res) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const message = formatApiError(data?.message || data?.error || data) || res.statusText || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data || {};
    if (data && (data.code || data.error)) err.code = formatApiError(data.code || data.error);
    throw err;
  }
  return data === null ? {} : data;
}

function inferRoleFromPath(path) {
  const p = String(path || "").split("?")[0];

  // Клиентские endpoints часто вызываются без явной роли.
  // Здесь принудительно выбираем clientToken, чтобы provider/admin token
  // из localStorage не ломал клиентский кабинет и избранное.
  if (
    p.startsWith("/api/clients") ||
    p.startsWith("/api/client/") ||
    p === "/api/client" ||
    p.startsWith("/api/wishlist")
  ) {
    return "client";
  }

  return null;
}

function buildHeaders(withAuthOrRole, path = "") {
  const base = { "Content-Type": "application/json" };
  if (withAuthOrRole === false) return base;

  const explicitRole =
    withAuthOrRole === "client" ||
    withAuthOrRole === "provider" ||
    withAuthOrRole === "admin"
      ? withAuthOrRole
      : null;

  const role = explicitRole || inferRoleFromPath(path);

  return { ...base, ...getAuthHeaders(role) };
}


/**
 * ✅ Safe JSON stringify:
 * - DO NOT touch normal strings (like "2026-02-08")
 * - If a Date object is present anywhere in body -> serialize as local YYYY-MM-DD
 *   (prevents timezone shift / -1 day bugs)
 */
function safeJsonStringify(obj) {
  return JSON.stringify(obj ?? {}, (key, value) => {
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return value;
  });
}

export async function apiGet(path, withAuthOrRole = true) {
  const res = await fetch(buildUrl(path), {
    headers: buildHeaders(withAuthOrRole, path),
    credentials: "include",
  });
  return handle(res);
}

export async function apiPost(path, body, withAuthOrRole = true) {
  const res = await fetch(buildUrl(path), {
    method: "POST",
    headers: buildHeaders(withAuthOrRole, path),
    body: safeJsonStringify(body),
    credentials: "include",
  });
  return handle(res);
}

export async function apiPostForm(path, formData, withAuthOrRole = true) {
  const explicitRole =
    withAuthOrRole === "client" ||
    withAuthOrRole === "provider" ||
    withAuthOrRole === "admin"
      ? withAuthOrRole
      : null;
  const role = explicitRole || inferRoleFromPath(path);
  const headers = withAuthOrRole === false ? {} : getAuthHeaders(role);
  const res = await fetch(buildUrl(path), {
    method: "POST",
    headers,
    body: formData,
    credentials: "include",
  });
  return handle(res);
}

export async function apiPut(path, body, withAuthOrRole = true) {
  const res = await fetch(buildUrl(path), {
    method: "PUT",
    headers: buildHeaders(withAuthOrRole, path),
    body: safeJsonStringify(body),
    credentials: "include",
  });
  return handle(res);
}

export async function apiPatch(path, body, withAuthOrRole = true) {
  const res = await fetch(buildUrl(path), {
    method: "PATCH",
    headers: buildHeaders(withAuthOrRole, path),
    body: safeJsonStringify(body),
    credentials: "include",
  });
  return handle(res);
}

export async function apiDelete(path, bodyOrRole, withAuthOrRole = true) {
  // Поддержка:
  // apiDelete(url, {..})
  // apiDelete(url, null)
  // apiDelete(url, "admin")  ✅ (роль без body)
  // apiDelete(url, {..}, "admin")

  let body = bodyOrRole;
  let auth = withAuthOrRole;

  if (
    typeof bodyOrRole === "string" &&
    (bodyOrRole === "client" || bodyOrRole === "provider" || bodyOrRole === "admin") &&
    withAuthOrRole === true
  ) {
    auth = bodyOrRole;
    body = undefined;
  }

  const res = await fetch(buildUrl(path), {
    method: "DELETE",
    headers: buildHeaders(auth, path),
    body: body ? safeJsonStringify(body) : undefined,
    credentials: "include",
  });
  return handle(res);
}
